/**
 * SPRINT-035B: Redirect return page (Vite SPA).
 *
 * Merchants configure return_url pointing here. After the provider redirect,
 * the user lands here. We NEVER assume success based on the redirect alone —
 * we re-fetch the session to verify (INVARIANT 12: webhook is authoritative).
 *
 * URL: /sessions/{sessionId}/return?session_id=xxx
 * OR:  /sessions/{sessionId}/return (sessionId from path)
 *
 * Flow:
 * 1. Read sessionId from path
 * 2. Get client secret from sessionStorage (stored before redirect)
 * 3. Fetch session to check actual status
 * 4. Redirect to main checkout page which handles polling/verification
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClientSecret, clearRedirectInfo } from '@/lib/secret';

export default function ReturnPage() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) return;

    const secret = getClientSecret(sessionId);
    clearRedirectInfo(sessionId);

    if (!secret) {
      // No secret available — can't authenticate; send back to root
      navigate('/', { replace: true });
      return;
    }

    // Return to main checkout page — it will re-fetch the session and
    // handle success/failure/polling as appropriate.
    // Pass secret via fragment so it's available for re-init.
    // react-router doesn't support fragment navigation, use window.location.replace
    window.location.replace(`/sessions/${sessionId}#cs=${encodeURIComponent(secret)}`);
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Verificando resultado del pago...</p>
      </div>
    </div>
  );
}
